import DatePicker, { type ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale/ja';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import 'react-datepicker/dist/react-datepicker.css';
import './DateRangeCalendar.scss';

export type DateRangeValue = {
  start: Date | null;
  end: Date | null;
};

type Props = {
  value: DateRangeValue;
  onChange: (range: DateRangeValue) => void;
  monthsShown?: number;
};

export default function DateRangeCalendar({ value, onChange, monthsShown = 2 }: Props) {
  const renderCustomHeader = ({
    monthDate,
    customHeaderCount,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }: ReactDatePickerCustomHeaderProps) => {
    const isFirst = customHeaderCount === 0;
    const isLast = customHeaderCount === monthsShown - 1;

    return (
      <div className="flex items-center justify-between py-1">
        {isFirst ? (
          <button
            type="button"
            disabled={prevMonthButtonDisabled}
            onClick={decreaseMonth}
            className="inline-flex size-7 items-center justify-center rounded border-0 bg-transparent text-sub hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="前の月へ"
          >
            <MdChevronLeft className="size-4" />
          </button>
        ) : (
          <span className="size-7" />
        )}
        <span className="text-sm font-medium text-main">
          {format(monthDate, 'yyyy年M月', { locale: ja })}
        </span>
        {isLast ? (
          <button
            type="button"
            disabled={nextMonthButtonDisabled}
            onClick={increaseMonth}
            className="inline-flex size-7 items-center justify-center rounded border-0 bg-transparent text-sub hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="次の月へ"
          >
            <MdChevronRight className="size-4" />
          </button>
        ) : (
          <span className="size-7" />
        )}
      </div>
    );
  };

  return (
    <DatePicker
      inline
      selectsRange
      swapRange
      locale={ja}
      monthsShown={monthsShown}
      selected={value.start}
      startDate={value.start ?? undefined}
      endDate={value.end ?? undefined}
      onChange={(dates) => {
        const [start, end] = dates as [Date | null, Date | null];
        onChange({ start, end });
      }}
      calendarClassName="date-range-calendar"
      renderCustomHeader={renderCustomHeader}
    />
  );
}
